// "USE OEM QUOTE PRICE" (oem-quote-register, 2026-10-06).
//
// Lists the OEM quote lines a draft line may take its net cost from: the SAME
// OEM and the EXACT normalised part number, nothing fuzzy -- the server decides
// the match (/anixify/api/oem_quote_matches) and this only shows it. Each match
// carries its amber warnings; none of them blocks the pick.
//
// Picking fills the line's net cost with the OEM price, its lead time, and the
// link. The engine then prices from that net cost exactly as from a typed one.

import { useEffect, useState } from "react";
import { oemQuoteMatches, type OemQuoteMatch } from "@/services/api";
import type { Material } from "@/types";
import { formatDate, moneyLocale } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, layout, typography, shadows } from "@/styles/tokens";

const BUTTON: React.CSSProperties = {
  height: heights.control, padding: `0 ${spacing[14]}px`,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  fontSize: typography.fontSize.base, backgroundColor: semanticColors.surface,
  color: semanticColors.textPrimary, cursor: "pointer", whiteSpace: "nowrap",
};
const PRIMARY: React.CSSProperties = {
  ...BUTTON, border: "none", backgroundColor: colors.indigo600,
  color: semanticColors.onAccent, fontWeight: typography.fontWeight.medium,
};
const NOTE: React.CSSProperties = { fontSize: typography.fontSize.sm, color: semanticColors.textSecondary };
const WARN: React.CSSProperties = {
  fontSize: typography.fontSize.sm, color: colors.amber800, backgroundColor: semanticColors.warningBg,
  border: `1px solid ${semanticColors.warningBorder}`, borderRadius: radius.base,
  padding: `${spacing[4]}px ${spacing[8]}px`,
};

export function OemQuoteDialog({ line, quoteId, onPick, onUnlink, onClose }: {
  line: Material;
  quoteId?: number;
  onPick: (match: OemQuoteMatch) => void;
  onUnlink?: () => void;
  onClose: () => void;
}) {
  const [matches, setMatches] = useState<OemQuoteMatch[] | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let live = true;
    void oemQuoteMatches({ oem: line.oem, partNumber: line.partNo, qty: line.qty, quoteId })
      .then((r) => {
        if (!live) return;
        if (!r.ok) { setError(r.error ?? "The OEM quotes could not be read."); return; }
        setMatches(r.matches ?? []);
      });
    return () => { live = false; };
  }, [line.oem, line.partNo, line.qty, quoteId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const linkedId = line.discountSource === "oem_quote" ? line.oemQuote?.line_id : undefined;

  return (
    <div
      data-testid="oem-dialog-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50, backgroundColor: semanticColors.overlay,
        display: "flex", alignItems: "center", justifyContent: "center", padding: spacing[16],
      }}
    >
      <div
        role="dialog"
        aria-label="Use OEM quote price"
        data-testid="oem-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: layout.pickerWidth, maxHeight: "90vh", overflowY: "auto",
          backgroundColor: semanticColors.surface, borderRadius: radius.base, boxShadow: shadows.modal,
          padding: spacing[16], display: "flex", flexDirection: "column", gap: spacing[12],
        }}
      >
        <div>
          <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold,
            color: semanticColors.textPrimary }}>Use OEM quote price</div>
          <div style={NOTE}>
            Line {line.lineNo}: {line.oem || "no OEM"} · {line.partNo || "no part number"} · qty {line.qty}.
            Only OEM quotes from the same OEM for exactly this part number are listed.
          </div>
        </div>

        {error && <div data-testid="oem-dialog-error" style={{ ...NOTE, color: semanticColors.destructive }}>{error}</div>}
        {!error && matches === null && <div style={NOTE}>Looking…</div>}
        {matches && matches.length === 0 && (
          <div data-testid="oem-dialog-empty" style={NOTE}>
            No OEM quote from {line.oem || "this OEM"} for {line.partNo || "this part"}. Add one under OEM Quotes.
          </div>
        )}
        {matches?.map((m) => (
          <div key={m.id} data-testid={`oem-match-${m.id}`} style={{
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base, padding: spacing[12],
            display: "flex", flexDirection: "column", gap: spacing[6],
          }}>
            <div style={{ display: "flex", gap: spacing[8], alignItems: "baseline", flexWrap: "wrap" }}>
              <strong style={{ color: semanticColors.textPrimary }}>{m.oem_quote.number}</strong>
              <span style={NOTE}>{m.oem_quote.project_name ?? ""}</span>
              <span style={{ flex: 1 }} />
              <span data-testid={`oem-match-${m.id}-price`} style={{ fontVariantNumeric: "tabular-nums" }}>
                {m.oem_quote.currency} {m.unit_price.toLocaleString(moneyLocale(), { maximumFractionDigits: 4 })}
              </span>
            </div>
            <div style={NOTE}>
              Qty {m.qty}{m.uom ? ` ${m.uom}` : ""} · valid until{" "}
              {m.oem_quote.valid_until ? formatDate(m.oem_quote.valid_until) : "—"}
              {m.lead_time ? ` · lead time ${m.lead_time}` : ""}
            </div>
            {m.warnings.map((w) => (
              <div key={w.kind} data-testid={`oem-match-${m.id}-warn-${w.kind}`} style={WARN}>{w.text}</div>
            ))}
            <div>
              {linkedId === m.id ? (
                <span data-testid={`oem-match-${m.id}-current`} style={NOTE}>In use on this line.</span>
              ) : (
                <button data-testid={`oem-match-${m.id}-use`} style={PRIMARY} onClick={() => onPick(m)}>
                  Use this price
                </button>
              )}
            </div>
          </div>
        ))}

        <div style={{ display: "flex", gap: spacing[8], justifyContent: "flex-end" }}>
          {linkedId !== undefined && onUnlink && (
            <button data-testid="oem-dialog-unlink" style={BUTTON} onClick={onUnlink}>
              Stop using the OEM quote (keep the cost as typed)
            </button>
          )}
          <button data-testid="oem-dialog-close" style={BUTTON} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
